import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const sessionId = searchParams.get("session_id");

  const pollStatus = useCallback(async (attempts = 0) => {
    if (!sessionId || attempts >= 6) {
      if (attempts >= 6) setStatus("timeout");
      return;
    }
    try {
      const res = await api.get(`/payments/status/${sessionId}`);
      setData(res.data);
      if (res.data.payment_status === "paid") {
        setStatus("success");
      } else if (res.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => pollStatus(attempts + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(attempts + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { pollStatus(); }, [pollStatus]);

  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="payment-success-page">
      {status === "checking" && (
        <div>
          <Loader2 className="w-12 h-12 animate-spin text-court-orange mx-auto mb-4" />
          <h2 className="font-heading text-2xl uppercase tracking-tight mb-2">Processing Payment</h2>
          <p className="text-muted-foreground">Confirming your payment...</p>
        </div>
      )}
      {status === "success" && (
        <div>
          <CheckCircle className="w-16 h-16 text-lime-volt mx-auto mb-4" />
          <h2 className="font-heading text-3xl uppercase tracking-tight mb-2" data-testid="payment-confirmed">Game On!</h2>
          <p className="text-muted-foreground mb-4">Your booking is confirmed. See you on the court!</p>
          {/* Payment Details */}
          <div className="border-2 border-slate-200 rounded-sm p-4 text-left text-sm max-w-xs mx-auto mb-6 space-y-2" data-testid="payment-details-card">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Payment Mode</span>
              <span className="font-medium">Stripe Checkout</span>
            </div>
            {data?.amount_total > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount Paid</span>
                <span className="font-heading font-bold text-green-600">{"\u20B1"}{(data.amount_total / 100).toLocaleString()}</span>
              </div>
            )}
            {sessionId && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Transaction ID</span>
                <span className="font-mono text-[10px] bg-slate-100 px-2 py-0.5 rounded" data-testid="stripe-session-id">
                  {sessionId.length > 20 ? `${sessionId.slice(0, 20)}...` : sessionId}
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {data?.booking_id && (
              <Button data-testid="view-receipt-btn" variant="outline" onClick={() => navigate(`/receipts/booking/${data.booking_id}`)} className="rounded-full border-2 px-8">
                View Full Receipt
              </Button>
            )}
            <Button data-testid="view-bookings-btn" onClick={() => navigate("/bookings")}
              className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide px-8 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
              View My Bookings
            </Button>
          </div>
        </div>
      )}
      {(status === "expired" || status === "timeout") && (
        <div>
          <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h2 className="font-heading text-2xl uppercase tracking-tight mb-2">Payment Issue</h2>
          <p className="text-muted-foreground mb-6">{status === "expired" ? "Payment session expired." : "Could not confirm payment."}</p>
          <Button data-testid="retry-btn" onClick={() => navigate("/bookings")} className="rounded-full">View Bookings</Button>
        </div>
      )}
    </div>
  );
}
